import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { CircleAlertIcon, LockIcon, PackageXIcon, RefreshCwIcon, RotateCcwIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Button } from '@ems/ui/components/button'

import { PageState } from '#admin/components/page-state.tsx'
import { moduleLabel } from '#admin/features/access/api.ts'

/**
 * What a failure looks like, as a page with a way out.
 *
 * It branches on the **code**, never on the message. That is the whole reason the API has a
 * refusal vocabulary: "this module is not on your plan", "your plan is read-only" and "you do not
 * have permission" need three different screens and three different next steps, and a client that
 * string-matched prose would get all three wrong the first time somebody reworded an error.
 *
 * Mounted as the router's `defaultErrorComponent`, so it renders in the route that failed — inside
 * the console frame, with the navigation still beside it — rather than replacing the whole app.
 */
export function ErrorView({ error, reset }: { error: unknown; reset?: () => void }) {
  const router = useRouter()
  const { reset: resetQueries } = useQueryErrorResetBoundary()

  const retry = (
    <Button
      onClick={() => {
        // Both halves: the query boundary forgets the failed query, and the router re-runs the
        // loaders. Either alone renders the same error again from cache.
        resetQueries()
        reset?.()
        void router.invalidate()
      }}
    >
      <RotateCcwIcon />
      Try again
    </Button>
  )

  if (error instanceof ApiClientError) {
    if (error.isMissingFeature) {
      return (
        <PageState icon={<PackageXIcon />} title="Not part of this edition’s plan">
          This edition does not include that module, so there is nothing here to show. An owner of
          the edition can ask for it to be added.
        </PageState>
      )
    }

    if (error.isReadOnly) {
      return (
        <PageState icon={<LockIcon />} title="This module is read-only">
          You can view its data but not change it while the edition&rsquo;s plan holds it read-only.
        </PageState>
      )
    }

    if (error.code === 'forbidden') {
      return (
        <PageState icon={<LockIcon />} title="You don’t have access to this page" offerSignOut>
          <p>{forbiddenReason(error)}</p>
          <p className="mt-2">
            Ask an owner of this edition to give your role access, or sign in with an account that
            has it.
          </p>
        </PageState>
      )
    }

    if (error.code === 'response_shape') {
      // Deploy skew. Saying so plainly beats a generic failure, because reloading genuinely
      // fixes it once CloudFront has caught up.
      return (
        <PageState
          icon={<RefreshCwIcon />}
          title="This page is out of date"
          actions={
            <Button onClick={() => globalThis.location.reload()}>
              <RefreshCwIcon />
              Reload
            </Button>
          }
        >
          The console and the API are briefly out of step after a release. Reloading fixes it.
        </PageState>
      )
    }

    return (
      <PageState icon={<CircleAlertIcon />} title="That did not work" actions={retry}>
        {error.message}
      </PageState>
    )
  }

  return (
    <PageState icon={<CircleAlertIcon />} title="Something went wrong" actions={retry}>
      An unexpected error stopped this page loading. Trying again usually works; if it keeps
      happening, the page may be broken rather than busy.
    </PageState>
  )
}

/**
 * Which access was missing, in words, from the `required` grant the API names.
 *
 * `billing.manage` → "permission to manage Billing". A refusal that carries its own reason (`detail.code`, as
 * the role routes send) says that instead, because it is more specific than any paraphrase.
 */
function forbiddenReason(error: ApiClientError): string {
  if (typeof error.detail?.['code'] === 'string') return error.message

  const required = error.detail?.['required']
  if (typeof required !== 'string') return 'Your role in this edition does not include it.'
  if (required.startsWith('platform:')) return 'This page is for platform staff only.'

  const [module, tier] = required.split('.')
  if (module === '*') return 'It needs full control of the edition, which only owners have.'
  const verb = tier === 'manage' ? 'manage' : tier === 'write' ? 'edit' : 'view'
  return `It needs permission to ${verb} ${moduleLabel(module ?? required)}, which your role in this edition does not include.`
}
